import React, { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { isLowPowerClient } from "../../lib/platform";

const prefersReduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Premium momentum smooth-scroll for the whole landing page.
// Also intercepts in-page anchor clicks so they glide instead of jump.
// IMPORTANT: disabled on phones / touch / reduced-motion — JS-driven smooth scroll
// hijacks native touch scrolling and feels sluggish/laggy on mobile. Native scroll wins there.
export function useLenis() {
  useEffect(() => {
    if (prefersReduced() || isLowPowerClient()) return;
    const lenis = new Lenis({
      duration: 1.15,
      lerp: 0.1,
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.4,
    });

    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const onClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute("href");
      if (!id || id.length < 2) return;
      const el = document.querySelector(id);
      if (!el) return;
      e.preventDefault();
      lenis.scrollTo(el, { offset: -80, duration: 1.3 });
    };
    document.addEventListener("click", onClick);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("click", onClick);
      lenis.destroy();
    };
  }, []);
}

// Glowing, hairline divider between sections — no hard lines, a soft light seam.
export function Divider({ className = "" }) {
  return (
    <div aria-hidden className={`relative mx-auto w-full max-w-6xl px-5 lg:px-8 ${className}`} data-testid="section-divider">
      <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-1.5 w-28 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand/25 blur-md" />
    </div>
  );
}

// Slim gradient rail at the very top that fills as you scroll the page.
export function ScrollProgress() {
  if (isLowPowerClient()) return null; // no scroll/spring work on phones
  return <ScrollProgressBar />;
}
function ScrollProgressBar() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  return (
    <motion.div
      aria-hidden
      data-testid="scroll-progress-rail"
      style={{ scaleX }}
      className="fixed inset-x-0 top-0 z-[60] h-[3px] origin-left bg-gradient-to-r from-brand-soft via-brand to-brand-deep"
    />
  );
}

// Wrap any block to make it drift vertically as it passes through the viewport.
export function Parallax({ children, speed = 60, className = "", as = "div" }) {
  const ref = useRef(null);
  const lite = isLowPowerClient();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [speed, -speed]);
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag ref={ref} style={lite ? undefined : { y }} className={`relative ${className}`}>
      {children}
    </MotionTag>
  );
}

// Floating "Start free" pill that appears once past the hero.
export function StickyCTA({ onJoin }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 720);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.button
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          onClick={onJoin}
          data-testid="sticky-cta"
          className="btn-primary fixed bottom-5 left-1/2 z-40 inline-flex -translate-x-1/2 items-center gap-2 text-sm shadow-[0_16px_50px_rgba(106,166,255,0.5)]"
        >
          Start free — 30 days <ArrowRight size={16} />
        </motion.button>
      )}
    </AnimatePresence>
  );
}
